import { useEffect, useState } from 'react';

import { ButtonCircular } from '@deliveryhero/pd-cookbook/components/ButtonCircular';
import { Button } from '@deliveryhero/pd-cookbook/components/ButtonV2';
import { Checkbox } from '@deliveryhero/pd-cookbook/components/Checkbox';
import { ComboTag } from '@deliveryhero/pd-cookbook/components/ComboTag';
import { Divider } from '@deliveryhero/pd-cookbook/components/Divider';
import { Grid } from '@deliveryhero/pd-cookbook/components/Grid';
import { GridItem } from '@deliveryhero/pd-cookbook/components/GridItem';
import IcCalendar from '@deliveryhero/pd-cookbook/components/Icons/IcCalendar';
import IcClose from '@deliveryhero/pd-cookbook/components/Icons/IcClose';
import IcGift from '@deliveryhero/pd-cookbook/components/Icons/IcGift';
import IcInfo from '@deliveryhero/pd-cookbook/components/Icons/IcInfo';
import IcLocationPin from '@deliveryhero/pd-cookbook/components/Icons/IcLocationPin';
import IcMinimumDeliveryFee from '@deliveryhero/pd-cookbook/components/Icons/IcMinimumDeliveryFee';
import IcMinus from '@deliveryhero/pd-cookbook/components/Icons/IcMinus';
import IcPlus from '@deliveryhero/pd-cookbook/components/Icons/IcPlus';
import IcSuccessFilled from '@deliveryhero/pd-cookbook/components/Icons/IcSuccessFilled';
import IcWarning from '@deliveryhero/pd-cookbook/components/Icons/IcWarning';
import { Link } from '@deliveryhero/pd-cookbook/components/Link';
import { Message } from '@deliveryhero/pd-cookbook/components/Message';
import { Pill } from '@deliveryhero/pd-cookbook/components/Pill';
import { Switch } from '@deliveryhero/pd-cookbook/components/Switch';
import { TextArea } from '@deliveryhero/pd-cookbook/components/TextArea';
import { TextInput } from '@deliveryhero/pd-cookbook/components/TextInput';
import { Tooltip } from '@deliveryhero/pd-cookbook/components/Tooltip';
import { Typography } from '@deliveryhero/pd-cookbook/components/Typography';
import { FieldArray, FormikHelpers } from 'formik';
import { GiftCardProduct, GiftCardRecipientType } from 'services/api';
import { GiftCardAction } from 'src/components/GiftCard/types';

import FormContainer from '../FormContainer';

import { FormLabelText } from './constants';
import { GiftCardPurchaseFormProps, GiftCardPurchaseFormValues } from './types';
import { getGiftCardPurchaseFormSchema } from './validation';

import './styles.scss';

const CHARACTER_LIMIT = 250;
const MAX_ROWS = 9;

export const GiftCardPurchaseForm = (props: GiftCardPurchaseFormProps) => {
  const {
    config,
    csvRecipients,
    data,
    detailFormRef,
    enableSubmit,
    handleCsvUploadCancel,
    handleFormReset,
    handleOrderSummaryChange,
    initialValues,
    onActionClick,
    onSubmit,
    selectedCard,
    toggleSubmit
  } = props;

  const country = config.name;
  const currencyCode = config.currencyCode;
  const brandName = config.entity.name;
  const indexUrl = config.index;
  const product: GiftCardProduct = data[0]; // currently only supports 1 product
  const denominations = product.value.denominations;

  const [isMaxRow, setIsMaxRow] = useState<boolean>(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [useCsv, setUseCsv] = useState(false);

  const showTooltip = () => setIsTooltipOpen(true);

  const hideTooltip = () => setIsTooltipOpen(false);

  const handleSubmit = (
    values: GiftCardPurchaseFormValues,
    actions: FormikHelpers<GiftCardPurchaseFormValues>
  ) => {
    const value = parseInt(values.value as string);
    const quantity = parseInt(values.quantity as string);

    let data = {
      ...values,
      value: value,
      theme: selectedCard.sku,
      use_csv: useCsv
    };

    if (useCsv) {
      const recipientCount = csvRecipients?.length as number;
      data = {
        ...data,
        recipients: [],
        quantity: recipientCount
      };
    } else if (values.distribute_myself) {
      data = {
        ...data,
        quantity: quantity,
        recipients: [],
        message: FormLabelText.MESSAGE_DEFAULT
      };
    } else {
      const recipientCount = values.recipients.length;
      data = {
        ...data,
        quantity: recipientCount,
        recipients: values.recipients
      };
    }
    onSubmit(data, actions);
  };

  const processNumericInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === '') return;

    const inputNumber = parseInt(e.target.value);

    if (inputNumber < 1 || isNaN(inputNumber)) e.target.value = '';
    else e.target.value = inputNumber.toString();
  };

  const termsAndConditions = [
    {
      icon: <IcCalendar />,
      text: FormLabelText.TITLE_TNC_LINE_1.replace(
        '%duration_period%',
        product.expiry
      )
    },
    {
      icon: <IcMinimumDeliveryFee />,
      text: FormLabelText.TITLE_TNC_LINE_2
    },
    {
      icon: <IcGift />,
      text: FormLabelText.TITLE_TNC_LINE_3
    },
    {
      icon: <IcLocationPin />,
      text: FormLabelText.TITLE_TNC_LINE_4.replace('%country%', country)
    },
    {
      icon: <IcWarning />,
      text: `${brandName.toLowerCase()} ${FormLabelText.TITLE_TNC_LINE_5}`
    }
  ];

  const renderTnC = (icon: JSX.Element, text: string, index: number) => {
    return (
      <div key={`tnc_${index}`} className="gift-card-form-container__tnc">
        <div className="gift-card-form-container__tnc__svg-container">
          {icon}
        </div>
        <Typography type="paragraph-xs" color="neutral-primary" marginLeft="xs">
          {text}
        </Typography>
      </div>
    );
  };

  useEffect(() => {
    handleFormReset();
  }, []);

  useEffect(() => {
    const isFileValid = csvRecipients && csvRecipients.file;
    isFileValid ? setUseCsv(true) : setUseCsv(false);
    isFileValid &&
      handleOrderSummaryChange({
        quantity: csvRecipients.length
      });
  }, [csvRecipients]);

  return (
    <FormContainer
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={getGiftCardPurchaseFormSchema({
        useCsv,
        currencyCode,
        productValue: product.value
      })}
      formRef={detailFormRef}
    >
      {({
        handleChange,
        values,
        touched,
        errors,
        setFieldValue,
        setFieldTouched
      }) => (
        <Grid className="gift-card-form-container">
          <div className="gift-card-spotlight">
            <div className="gift-card-spotlight-form-value"></div>
            <GridItem
              gridColumn="1"
              className="gift-card-form-container__title"
            >
              <Typography type="title-md" color="neutral-primary">
                {FormLabelText.TITLE_GIFT_CARD}
              </Typography>
              <Typography
                type="title-md"
                color="interaction-primary"
                marginLeft="xxs"
              >
                {FormLabelText.MANDATORY}
              </Typography>
            </GridItem>
            <GridItem gridColumn="1">
              <Typography
                type="paragraph-sm"
                color="system-information"
                marginTop="xxs"
                marginBottom="sm"
              >
                {FormLabelText.TITLE_GIFT_CARD_INFORMATION}
              </Typography>
            </GridItem>
            <GridItem gridColumn="1">
              <TextInput
                id="value"
                name="value"
                label={FormLabelText.GIFT_CARD_VALUE}
                type="text"
                min={product.value.min}
                max={product.value.max}
                value={values.value}
                onChange={(e) => {
                  processNumericInput(e);
                  setFieldTouched('value', false);
                  handleChange(e);
                  handleOrderSummaryChange({
                    giftCardValue: e.target.value ? parseInt(e.target.value) : 0
                  });
                }}
                invalid={touched.value && Boolean(errors.value)}
                isError={touched.value && Boolean(errors.value)}
                errorMessage={(touched.value && errors.value) || ''}
              />
            </GridItem>
            <GridItem
              gridColumn="1"
              className="gift-card-form-container__pills"
              data-testid="denomination-pills"
            >
              {denominations.map((denomination, index) => (
                <Pill
                  key={index}
                  id="value"
                  checked={values.value == denomination}
                  label={denomination.toString()}
                  onChange={() => {
                    handleOrderSummaryChange({
                      giftCardValue: denomination
                    });
                    setFieldValue('value', denomination);
                  }}
                />
              ))}
            </GridItem>
          </div>
          <div className="gift-card-spotlight">
            <div className="gift-card-spotlight-form-recipients"></div>
            <GridItem
              gridColumn="1"
              className="gift-card-form-container__title"
            >
              <Typography type="title-md" color="neutral-primary">
                {FormLabelText.TITLE_SEND}
              </Typography>
              <Typography
                type="title-md"
                color="interaction-primary"
                marginLeft="xxs"
              >
                {FormLabelText.MANDATORY}
              </Typography>
            </GridItem>
            <GridItem gridColumn="1">
              <Typography
                type="label-lg"
                color="neutral-primary"
                marginTop="xxs"
                marginBottom="xxs"
              >
                {FormLabelText.TITLE_SEND_SUBTITLE}
              </Typography>
            </GridItem>
            <GridItem gridColumn="1">
              <Typography
                type="paragraph-sm"
                color="system-information"
                marginBottom="sm"
              >
                {FormLabelText.TITLE_SEND_INFORMATION}
              </Typography>
            </GridItem>
            <FieldArray name="recipients">
              {({ remove, push }) => (
                <>
                  {values.recipients.map((recipient, index) => (
                    <Grid
                      gridTemplateColumns="11fr 11fr 1fr"
                      columnGap="16px"
                      key={index}
                    >
                      <GridItem>
                        <TextInput
                          id={`recipients.${index}.name`}
                          name={`recipients.${index}.name`}
                          label={FormLabelText.NAME}
                          value={recipient.name}
                          onChange={(e) => {
                            setFieldTouched(`recipients.${index}.name`, false);
                            setFieldValue(
                              `recipients.${index}.name`,
                              e.target.value
                            );
                          }}
                          disabled={values.distribute_myself || useCsv}
                          errorMessage={
                            (touched.recipients &&
                              touched.recipients[index] &&
                              touched.recipients[index].name &&
                              errors.recipients &&
                              errors.recipients[index] &&
                              (
                                errors.recipients[
                                  index
                                ] as GiftCardRecipientType
                              ).name) ||
                            ''
                          }
                          isError={Boolean(
                            touched.recipients &&
                              touched.recipients[index] &&
                              touched.recipients[index].name &&
                              errors.recipients &&
                              errors.recipients[index] &&
                              (
                                errors.recipients[
                                  index
                                ] as GiftCardRecipientType
                              ).name
                          )}
                          invalid={Boolean(
                            touched.recipients &&
                              touched.recipients[index] &&
                              touched.recipients[index].name &&
                              errors.recipients &&
                              errors.recipients[index] &&
                              (
                                errors.recipients[
                                  index
                                ] as GiftCardRecipientType
                              ).name
                          )}
                        />
                      </GridItem>
                      <GridItem>
                        <TextInput
                          id={`recipients.${index}.email`}
                          name={`recipients.${index}.email`}
                          label={FormLabelText.EMAIL}
                          value={recipient.email}
                          onChange={(e) => {
                            setFieldTouched(`recipients.${index}.email`, false);
                            setFieldValue(
                              `recipients.${index}.email`,
                              e.target.value
                            );
                          }}
                          disabled={values.distribute_myself || useCsv}
                          isError={Boolean(
                            touched.recipients &&
                              touched.recipients[index] &&
                              touched.recipients[index].email &&
                              errors.recipients &&
                              errors.recipients[index] &&
                              (
                                errors.recipients[
                                  index
                                ] as GiftCardRecipientType
                              ).email
                          )}
                          invalid={Boolean(
                            touched.recipients &&
                              touched.recipients[index] &&
                              touched.recipients[index].email &&
                              errors.recipients &&
                              errors.recipients[index] &&
                              (
                                errors.recipients[
                                  index
                                ] as GiftCardRecipientType
                              ).email
                          )}
                          errorMessage={
                            (touched.recipients &&
                              touched.recipients[index] &&
                              touched.recipients[index].email &&
                              errors.recipients &&
                              errors.recipients[index] &&
                              (
                                errors.recipients[
                                  index
                                ] as GiftCardRecipientType
                              ).email) ||
                            ''
                          }
                        />
                      </GridItem>
                      {index !== 0 ? (
                        <ButtonCircular
                          variant="contained"
                          size="small"
                          type="button"
                          disabled={values.distribute_myself || useCsv}
                          onClick={() => {
                            remove(index);
                            handleOrderSummaryChange({
                              quantity: values.recipients.length - 1
                            });
                            if (values.recipients.length - 1 < MAX_ROWS)
                              setIsMaxRow(false);
                          }}
                          aria-label="delete-recipient"
                          style={{ marginTop: '12px' }}
                        >
                          <IcMinus />
                        </ButtonCircular>
                      ) : (
                        <div style={{ paddingLeft: '38.5px' }}></div>
                      )}
                    </Grid>
                  ))}
                  <GridItem>
                    <Button
                      key="1"
                      data-testid="add-recipient-button"
                      type="button"
                      variant="text"
                      prefix={<IcPlus />}
                      disabled={isMaxRow || values.distribute_myself || useCsv}
                      onClick={() => {
                        push({ name: '', email: '' });
                        handleOrderSummaryChange({
                          quantity: values.recipients.length + 1
                        });
                        if (values.recipients.length + 1 === MAX_ROWS)
                          setIsMaxRow(true);
                      }}
                    >
                      {FormLabelText.ADD_RECIPIENT_BUTTON}
                    </Button>
                  </GridItem>
                </>
              )}
            </FieldArray>
            <div className="gift-card-form-container__divider">
              <Divider />
              <Typography
                type="label-md"
                color="neutral-primary"
                marginLeft="sm"
                marginRight="sm"
              >
                or
              </Typography>
              <Divider />
            </div>
            {csvRecipients && csvRecipients.file ? (
              <Grid rowGap="11px">
                <GridItem>
                  <ComboTag
                    id="csv-recipients-combo-tag"
                    primaryContent={
                      <>
                        <Typography type="paragraph-sm" color="neutral-primary">
                          {csvRecipients.file?.name}
                        </Typography>
                        <IcClose
                          fill="interaction-secondary"
                          data-testid="delete-csv-file-button"
                          width="16px"
                          cursor="pointer"
                          onClick={() => {
                            handleOrderSummaryChange({
                              quantity: values.recipients.length
                            });
                            handleCsvUploadCancel();
                          }}
                        />
                      </>
                    }
                  />
                </GridItem>
                <Grid columnGap="4px" gridTemplateColumns={'auto auto 1fr'}>
                  <IcSuccessFilled fill="success" />
                  <Typography type="label-md" color="success">
                    {csvRecipients.length > 1
                      ? `${csvRecipients.length} entries uploaded`
                      : `${csvRecipients.length} entry uploaded`}
                  </Typography>
                </Grid>
                <Typography type="label-sm-secondary" color="neutral-secondary">
                  {FormLabelText.UPLOAD_CSV_INFORMATION}
                </Typography>
              </Grid>
            ) : (
              <>
                <Typography type="paragraph-sm" color="system-information">
                  {FormLabelText.TITLE_CSV}
                </Typography>
                <GridItem>
                  <Button
                    key="2"
                    type="button"
                    data-testid="upload-csv-button"
                    marginTop="xs"
                    variant="secondary"
                    disabled={values.distribute_myself}
                    onClick={() => {
                      setFieldTouched('recipients', false);
                      setFieldTouched('message', false);
                      setFieldTouched('value', false);
                      setFieldTouched('quantity', false);
                      setFieldValue('recipients', initialValues.recipients);
                      onActionClick(GiftCardAction.CsvUpload);
                    }}
                  >
                    {FormLabelText.UPLOAD_CSV_BUTTON}
                  </Button>
                </GridItem>
              </>
            )}
            <div className="gift-card-form-container__divider">
              <Divider />
              <Typography
                type="label-md"
                color="neutral-primary"
                marginLeft="sm"
                marginRight="sm"
              >
                or
              </Typography>
              <Divider />
            </div>
            <GridItem
              gridColumn="1"
              className="gift-card-form-container__title"
            >
              <Typography type="label-lg" color="neutral-primary">
                {FormLabelText.TITLE_EMAIL}
              </Typography>
            </GridItem>
            <div className="gift-card-form-container__switch">
              <Typography type="label-sm" color="neutral-primary">
                {FormLabelText.TITLE_EMAIL_INFORMATION}
              </Typography>
              <Switch
                id="distribute-myself-switch"
                name="distribute-myself-switch"
                checked={values.distribute_myself}
                onChange={(e) => {
                  setFieldTouched('recipients', false);
                  setFieldTouched('message', false);
                  setFieldValue('distribute_myself', e.target.checked);
                  setFieldValue('recipients', initialValues.recipients);
                  setFieldValue('message', initialValues.message);
                  handleOrderSummaryChange({
                    quantity: e.target.checked ? values.quantity || 0 : 1,
                    giftCardMessage: FormLabelText.MESSAGE_DEFAULT
                  });
                  handleCsvUploadCancel();
                }}
              />
              <div
                data-testid="gift-card-tooltip"
                onMouseEnter={showTooltip}
                onMouseLeave={hideTooltip}
              >
                <Tooltip
                  isVisibleForAnimation={isTooltipOpen}
                  arrowAlignment="left"
                  bodyText={FormLabelText.TOOLTIP_TEXT}
                  placement="right"
                >
                  <IcInfo />
                </Tooltip>
              </div>
            </div>
            <GridItem
              gridColumn="1"
              className="gift-card-form-container__title"
            >
              <Typography
                type="title-md"
                color="neutral-primary"
                marginBottom="sm"
              >
                {FormLabelText.TITLE_QUANTITY}
                {values.distribute_myself
                  ? ' '
                  : !values.distribute_myself &&
                      csvRecipients &&
                      csvRecipients.file
                    ? `: ${csvRecipients.length}`
                    : `: ${values.recipients.length}`}
              </Typography>
              {values.distribute_myself && (
                <Typography
                  type="title-md"
                  color="interaction-primary"
                  marginLeft="xxs"
                >
                  {FormLabelText.MANDATORY}
                </Typography>
              )}
            </GridItem>
          </div>
          {values.distribute_myself && (
            <TextInput
              id="quantity"
              name="quantity"
              type="text"
              label={FormLabelText.TITLE_QUANTITY_INFORMATION}
              value={values.quantity}
              onChange={(e) => {
                setFieldTouched('quantity', false);
                processNumericInput(e);
                handleChange(e);
                handleOrderSummaryChange({
                  quantity: parseInt(e.target.value) || 0
                });
              }}
              invalid={touched.quantity && Boolean(errors.quantity)}
              isError={touched.quantity && Boolean(errors.quantity)}
              errorMessage={(touched.quantity && errors.quantity) || ''}
            />
          )}
          <div className="gift-card-spotlight">
            <div className="gift-card-spotlight-form-message"></div>
            <GridItem
              gridColumn="1"
              className="gift-card-form-container__title"
            >
              <Typography
                type="title-md"
                color="neutral-primary"
                marginTop="lg"
                marginBottom="sm"
              >
                {FormLabelText.TITLE_MESSAGE}
              </Typography>
              {!values.distribute_myself && (
                <Typography
                  type="title-md"
                  color="interaction-primary"
                  marginLeft="xxs"
                  marginTop="lg"
                >
                  {FormLabelText.MANDATORY}
                </Typography>
              )}
            </GridItem>
            <div className="message">
              <TextArea
                id="message"
                name="message"
                label={FormLabelText.MESSAGE_DEFAULT}
                onChange={(e) => {
                  setFieldTouched('message', false);
                  setFieldValue(
                    'message',
                    e.target.value.slice(0, CHARACTER_LIMIT)
                  );
                  handleOrderSummaryChange({
                    giftCardMessage: e.target.value.slice(0, CHARACTER_LIMIT)
                  });
                }}
                value={values.message}
                rows={3}
                errorMessage={
                  (Boolean(errors.message) &&
                    touched.message &&
                    errors.message) ||
                  ''
                }
                invalid={touched.message && Boolean(errors.message)}
                isError={touched.message && Boolean(errors.message)}
                disabled={values.distribute_myself}
              />
            </div>
            <Typography
              type="label-sm-secondary"
              color={
                values.distribute_myself
                  ? 'neutral-inactive'
                  : 'neutral-secondary'
              }
              marginTop={errors.message ? 'sm' : 'xxxs'}
            >
              {`${values.message.length}/${CHARACTER_LIMIT} characters`}
            </Typography>
          </div>
          <GridItem gridColumn="1" className="gift-card-form-container__title">
            <Typography
              type="title-md"
              color="neutral-primary"
              marginTop="lg"
              marginBottom="sm"
            >
              {FormLabelText.TITLE_TNC}
            </Typography>
            <Typography
              type="title-md"
              color="interaction-primary"
              marginLeft="xxs"
              marginTop="lg"
            >
              {FormLabelText.MANDATORY}
            </Typography>
          </GridItem>
          {termsAndConditions.map((termsAndCondition, index) =>
            renderTnC(termsAndCondition.icon, termsAndCondition.text, index)
          )}
          <Checkbox
            id="tnc_checkbox"
            label={
              <>
                <Typography type="paragraph-sm" color="system-information">
                  {`I agree to the `}
                  <Link
                    href={`${indexUrl}/contents/terms-and-conditions.htm`}
                    target="_blank"
                  >
                    terms & conditions
                  </Link>
                  {` and `}
                  <Link
                    href={`${indexUrl}/contents/privacy.htm`}
                    target="_blank"
                  >
                    privacy policy
                  </Link>
                </Typography>
              </>
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              toggleSubmit(event.target.checked);
            }}
          />
          {!enableSubmit && (
            <Message
              style={{ marginTop: '16px' }}
              kind="error"
              message={FormLabelText.TNC_ERROR_TEXT}
            />
          )}
        </Grid>
      )}
    </FormContainer>
  );
};
